import { config } from '@iheartjane/config';
import type { AppMode, PriceId, ReservationMode } from '@iheartjane/types';
import { Storage } from '@iheartjane/utils';
import qs from 'qs';

import type { UserConsentItem } from '@jane/shared-ecomm/types';
// eslint-disable-next-line no-restricted-imports
import type {
  Cart,
  Location,
  PaymentMethod,
  Preferences,
} from '@jane/shared/models';
import type { Address, Tags } from '@jane/shared/types';

import { cartStorageKey } from '../customer/redux/cart';
import { request } from '../lib/request';

const buildUrl = (url = '') => `${config.apiPath}/carts${url}`;

interface CreateParamsProduct {
  count: number;
  price_id: PriceId;
  product_id: string | number;
}
interface CreateParams {
  app_mode?: AppMode;
  app_mode_name?: AppMode;
  external_source?: string;
  headless_checkout_partner_id?: string;
  products: CreateParamsProduct[];
  promo_code?: string;
  reservation_mode: ReservationMode;
  store_id: string | number;
  tags?: Tags;
  user_agent: string;
  user_group_special_id?: string | number;
}

interface UpdateParams {
  anonymous_user_id?: string;
  canpay_intent_id?: string;
  canpay_token?: string;
  currency_code?: string;
  current_url?: string;
  delivery_address?: Address | null;
  delivery_fee_amount?: number;
  delivery_finished_at?: string;
  delivery_started_at?: string;
  message?: string;
  moneris_ticket?: string | null;
  payfirma_card_cvv2?: string;
  payfirma_card_expiry_month?: string;
  payfirma_card_expiry_year?: string;
  payfirma_card_number?: string;
  payfirma_card_postal_code?: string;
  payment_method?: PaymentMethod;
  recaptcha_token?: string | null;
  reservation_mode?: ReservationMode;
  tags?: Tags;
  user_group_special_id?: string | number;
  user_id?: string | number | null;
  uuid: string;
}

interface CanPayApprovedIntentData {
  intent_id: string;
  response: string;
  signature: string;
}

export interface CheckoutRequest {
  aeropay_account_verified: boolean;
  aeropay_bank_account_id?: string;
  age_verification: {
    birth_date?: string;
    government_identification_url?: string | null;
  };
  anonymous_user_id?: string;
  canpay_token?: string;
  canpay_v2remotepay_transaction?: CanPayApprovedIntentData;
  current_url: string;
  customer: {
    email?: string;
    external_user_id?: string | null;
    name: string;
    phone?: string;
  };
  delivery_address?: Address;
  delivery_finished_at?: string;
  delivery_started_at?: string;
  headless_checkout_partner_id?: string;
  is_curbside_pickup: boolean;
  j_device_id?: string;
  medical_identification: {
    id_back_url?: string | null;
    id_front_url?: string | null;
    id_number?: string | null;
  };
  message: string;
  moneris_ticket?: string | null;
  payfirma_card_cvv2?: string;
  payfirma_card_expiry_month?: string;
  payfirma_card_expiry_year?: string;
  payfirma_card_number?: string;
  payfirma_card_postal_code?: string;
  payment_method: PaymentMethod;
  promotions_opt_out?: boolean;
  recaptcha_token?: string | null;
  reservation_mode: ReservationMode;
  square_payment_token?: string;
  stronghold_payment_source_id?: string;
  tags?: Tags;
  tip_amount: number;
  user_consent: UserConsentItem[];
  user_group_special_id?: string | number;
  user_location: Location;
  user_preferences: Preferences;
  uuid: string;
}

export interface CheckoutResponse {
  contact?: {
    is_registered: boolean;
  } | null;
  message: string;
  reservation: {
    amount: string | number;
    delivery_finished_at: number | null;
    delivery_started_at: number | null;
    id: number;
    message: string | null;
    payment_method: string | null;
    uuid: string;
  };
  warning_message?: string | null;
}

export const CartSource = {
  create(body: CreateParams) {
    return request(buildUrl(), {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  optimizeSpecials({
    user_group_special_id,
    reservation_mode,
    promo_code,
    uuid,
    delivery_address,
  }: {
    delivery_address?: Address | null;
    promo_code: string | undefined;
    reservation_mode: ReservationMode;
    user_group_special_id: string | number | undefined;
    uuid: string;
  }) {
    return request(buildUrl(`/optimize_specials`), {
      body: JSON.stringify({
        user_group_special_id,
        reservation_mode,
        promo_code,
        uuid,
        delivery_address,
      }),
      method: 'PATCH',
    });
  },

  getRemote(
    uuid: string,
    delivery_address?: Address | null,
    payment_method?: string | null
  ) {
    const queryParams = qs.stringify({
      uuid,
      delivery_address: delivery_address ? delivery_address : undefined,
      payment_method,
    });
    return request(buildUrl(`?${queryParams}`), {
      method: 'GET',
    });
  },

  getLocal(
    appMode: AppMode,
    storeId: string | number | undefined
  ): Promise<{ cart: Cart }> {
    const storeAppCart = Storage.get<Cart>(cartStorageKey(appMode, storeId));

    if (storeAppCart) {
      return Promise.resolve({ cart: storeAppCart });
    }

    return Promise.resolve({ cart: {} as Cart });
  },

  getForUser(
    app_mode: AppMode,
    store_id: string | number | undefined,
    uuid: string
  ) {
    const queryParams = qs.stringify({ uuid, app_mode, store_id });
    return request(buildUrl(`/get_for_user?${queryParams}`), {
      method: 'GET',
    });
  },

  getUnavailableProductIds(uuid: string) {
    return request(
      buildUrl(`/get_unavailable_product_ids?${qs.stringify({ uuid })}`),
      {
        method: 'GET',
      }
    );
  },

  delete(uuid: string) {
    return request(buildUrl(), {
      method: 'DELETE',
      body: JSON.stringify({ uuid }),
    });
  },

  update(cart: UpdateParams) {
    return request(buildUrl(), {
      body: JSON.stringify(cart),
      method: 'PATCH',
    });
  },

  async save(checkout: CheckoutRequest): Promise<CheckoutResponse> {
    const result = await request(buildUrl('/checkout'), {
      body: JSON.stringify(checkout),
      method: 'POST',
    });
    return result;
  },

  removeSpecialHasChangedFlag(uuid: string) {
    return request(buildUrl('/changed_special_flag'), {
      method: 'DELETE',
      body: JSON.stringify({ uuid }),
    });
  },

  lookupSalesTaxRate(
    address: Address,
    reservation_mode: ReservationMode,
    uuid: string
  ) {
    const { street, city, state_code, zipcode } = address;
    const queryParams = qs.stringify({
      street,
      city,
      state_code,
      zipcode,
      reservation_mode,
      uuid,
    });
    return request(buildUrl(`/lookup_sales_tax_rate?${queryParams}`), {
      method: 'GET',
    });
  },

  getBundlePossibilities(uuid: string, touched_product_id: string | number) {
    const queryParams = qs.stringify({
      uuid,
      touched_product_id,
    });
    return request(buildUrl(`/find_bundle_specials?${queryParams}`), {
      method: 'GET',
    });
  },
};
